/**
 * SMB Mapping Storage Utility
 *
 * Persists catalog and vendor field mappings to localStorage.
 * - Catalog mappings: stored by platform (structure is consistent across vendor files)
 * - Vendor mappings: stored by platform + filename (structure varies per vendor file)
 *
 * Allows users to skip re-mapping when re-uploading files or processing similar vendor files.
 */

import type { CatalogFieldMapping, SmbPlatform, TableFieldMapping, VendorSampleData } from './types'

const CATALOG_STORAGE_KEY_PREFIX = 'smb-catalog-mapping-'
const VENDOR_STORAGE_KEY_PREFIX = 'smb-vendor-mapping-'

/**
 * Get the localStorage key for catalog mappings (by platform)
 */
function getCatalogStorageKey(platform: SmbPlatform): string {
  return `${CATALOG_STORAGE_KEY_PREFIX}${platform}`
}

/**
 * Get the localStorage key for vendor mappings (by platform + filename)
 * Normalizes filename to handle minor variations (removes extension case differences)
 */
function getVendorStorageKey(platform: SmbPlatform, filename: string): string {
  // Normalize filename: lowercase, trim whitespace
  const normalizedFilename = filename.trim().toLowerCase()
  return `${VENDOR_STORAGE_KEY_PREFIX}${platform}-${normalizedFilename}`
}

/**
 * Save catalog mappings to localStorage
 */
export function saveCatalogMappings(
  platform: SmbPlatform,
  mappings: CatalogFieldMapping[]
): void {
  try {
    const data = {
      mappings,
      savedAt: new Date().toISOString(),
    }
    localStorage.setItem(getCatalogStorageKey(platform), JSON.stringify(data))
  } catch (error) {
    console.warn('Failed to save catalog mappings to localStorage:', error)
  }
}

/**
 * Load catalog mappings from localStorage
 * Only returns mappings if the saved column names exist in the current file's columns
 */
export function loadCatalogMappings(
  platform: SmbPlatform,
  currentColumns: string[]
): CatalogFieldMapping[] | null {
  try {
    const stored = localStorage.getItem(getCatalogStorageKey(platform))
    if (!stored) return null

    const data = JSON.parse(stored)
    const savedMappings: CatalogFieldMapping[] = data.mappings

    if (!savedMappings || !Array.isArray(savedMappings)) return null

    // Validate and filter: only keep mappings where columns still exist
    const validatedMappings = savedMappings.map((mapping) => {
      // Filter selectedColumns to only include columns that exist in current file
      const validColumns = mapping.selectedColumns.filter((col) =>
        currentColumns.includes(col)
      )
      return {
        ...mapping,
        selectedColumns: validColumns,
      }
    })

    // Check if we have at least some valid mappings
    const hasValidMappings = validatedMappings.some(
      (m) => m.selectedColumns.length > 0
    )

    return hasValidMappings ? validatedMappings : null
  } catch (error) {
    console.warn('Failed to load catalog mappings from localStorage:', error)
    return null
  }
}

/**
 * Clear saved catalog mappings for a platform
 */
export function clearCatalogMappings(platform: SmbPlatform): void {
  try {
    localStorage.removeItem(getCatalogStorageKey(platform))
  } catch (error) {
    console.warn('Failed to clear catalog mappings from localStorage:', error)
  }
}

// =============================================================================
// VENDOR MAPPING STORAGE
// =============================================================================

/**
 * Save vendor table field mappings to localStorage
 * Stored by platform + filename since vendor file structure varies
 */
export function saveVendorMappings(
  platform: SmbPlatform,
  filename: string,
  mappings: TableFieldMapping[]
): void {
  try {
    const data = {
      mappings,
      savedAt: new Date().toISOString(),
      filename, // Store original filename for debugging
    }
    localStorage.setItem(getVendorStorageKey(platform, filename), JSON.stringify(data))
  } catch (error) {
    console.warn('Failed to save vendor mappings to localStorage:', error)
  }
}

/**
 * Load vendor table field mappings from localStorage
 * Validates that saved table IDs and column names still exist in current sample data.
 * Also adds fresh empty mappings for any NEW tables in the current data that weren't saved.
 *
 * @param platform - SMB platform (square, quickbooks)
 * @param filename - Vendor filename
 * @param sampleData - Current vendor sample data from backend
 * @returns Validated mappings that can be applied, or null if no saved mappings exist
 */
export function loadVendorMappings(
  platform: SmbPlatform,
  filename: string,
  sampleData: VendorSampleData
): TableFieldMapping[] | null {
  try {
    const stored = localStorage.getItem(getVendorStorageKey(platform, filename))
    if (!stored) return null

    const data = JSON.parse(stored)
    const savedMappings: TableFieldMapping[] = data.mappings

    if (!savedMappings || !Array.isArray(savedMappings)) return null

    // Build a map of current table headers for validation
    const currentTableHeaders: Record<string, Set<string>> = {}
    Object.entries(sampleData.table_samples).forEach(([tableId, sample]) => {
      currentTableHeaders[tableId] = new Set(sample.header_values)
    })

    // Track which tables we've processed from saved mappings
    const processedTableIds = new Set<string>()

    // Validate and filter saved mappings
    const validatedMappings: TableFieldMapping[] = []
    let hasAnyValidMapping = false

    savedMappings.forEach((savedTable) => {
      // Check if this table still exists in current data
      const currentHeaders = currentTableHeaders[savedTable.tableId]
      if (!currentHeaders) return // Table no longer exists

      const currentSample = sampleData.table_samples[savedTable.tableId]
      if (!currentSample || currentSample.total_data_rows === 0) return // Empty table

      processedTableIds.add(savedTable.tableId)

      // Validate each mapping - only keep columns that still exist
      const validatedTableMappings: TableFieldMapping['mappings'] = {
        'Manufacturer SKU': null,
        'Vendor SKU': null,
        'UPC': null,
        'Cost': null,
        'MAP': null,
        'MSRP': null,
        'N/A': null,
      }

      Object.entries(savedTable.mappings).forEach(([fieldType, columnName]) => {
        if (columnName && currentHeaders.has(columnName)) {
          // Column still exists - keep the mapping
          validatedTableMappings[fieldType as keyof typeof validatedTableMappings] = columnName
          hasAnyValidMapping = true
        }
      })

      validatedMappings.push({
        tableId: savedTable.tableId,
        tabName: savedTable.tabName,
        totalRows: currentSample.total_data_rows, // Use current row count
        mappings: validatedTableMappings,
      })
    })

    // Add fresh empty mappings for NEW tables that weren't in saved data
    Object.entries(sampleData.table_samples).forEach(([tableId, sample]) => {
      // Skip if already processed from saved mappings
      if (processedTableIds.has(tableId)) return

      // Skip empty tables
      if (sample.total_data_rows === 0) return

      // Find tab name for this table
      let tabName = tableId
      for (const [tab, tableIds] of Object.entries(sampleData.tables_by_tab)) {
        if (tableIds.includes(tableId)) {
          tabName = tab
          break
        }
      }

      // Add fresh empty mappings for this new table
      validatedMappings.push({
        tableId,
        tabName,
        totalRows: sample.total_data_rows,
        mappings: {
          'Manufacturer SKU': null,
          'Vendor SKU': null,
          'UPC': null,
          'Cost': null,
          'MAP': null,
          'MSRP': null,
          'N/A': null,
        },
      })
    })

    return hasAnyValidMapping ? validatedMappings : null
  } catch (error) {
    console.warn('Failed to load vendor mappings from localStorage:', error)
    return null
  }
}

/**
 * Clear saved vendor mappings for a specific platform + filename
 */
export function clearVendorMappings(platform: SmbPlatform, filename: string): void {
  try {
    localStorage.removeItem(getVendorStorageKey(platform, filename))
  } catch (error) {
    console.warn('Failed to clear vendor mappings from localStorage:', error)
  }
}
